export interface DebugConfig {
  ENABLED: boolean;
  LOG_PERFORMANCE?: boolean;
  LOG_API?: boolean;
  LOG_STATE?: boolean;
  LOG_DATA?: boolean;
  LOG_CACHE?: boolean;
  PERFORMANCE_THRESHOLD_MS?: number;
}

export interface DebugLogger {
  info: (message: string, data?: unknown) => void;
  state: (message: string, data?: unknown) => void;
  api: (message: string, data?: unknown) => void;
  data: (message: string, data?: unknown) => void;
  cache: (message: string, data?: unknown) => void;
  success: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error: (message: string, error?: unknown) => void;
  startTimer: (label: string) => { label: string; start: number };
  endTimer: (timer: { label: string; start: number }, details?: Record<string, unknown>) => number;
  table: (label: string, data: unknown[]) => void;
  group: (label: string) => void;
  groupEnd: () => void;
}

function fmt(data: unknown): string {
  if (data === undefined || data === null) return '';
  if (typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean') return String(data);
  try { return JSON.stringify(data); } catch { return String(data); }
}

export function createDebugLogger(prefix: string, config: DebugConfig): DebugLogger {
  return {
    info: (message: string, data?: unknown): void => {
      if (config.ENABLED) {
        console.log(`${prefix} ${message}`, fmt(data));
      }
    },

    state: (message: string, data?: unknown): void => {
      if (config.ENABLED && config.LOG_STATE) {
        console.log(`${prefix} STATE: ${message}`, fmt(data));
      }
    },

    api: (message: string, data?: unknown): void => {
      if (config.ENABLED && config.LOG_API) {
        console.log(`${prefix} API: ${message}`, fmt(data));
      }
    },

    data: (message: string, data?: unknown): void => {
      if (config.ENABLED && config.LOG_DATA) {
        console.log(`${prefix} DATA: ${message}`, fmt(data));
      }
    },

    cache: (message: string, data?: unknown): void => {
      if (config.ENABLED && config.LOG_CACHE) {
        console.log(`${prefix} CACHE: ${message}`, fmt(data));
      }
    },

    success: (message: string, data?: unknown): void => {
      if (config.ENABLED) {
        console.log(`${prefix} ${message}`, fmt(data));
      }
    },

    warn: (message: string, data?: unknown): void => {
      if (config.ENABLED) {
        console.warn(`${prefix} ${message}`, fmt(data));
      }
    },

    error: (message: string, error?: unknown): void => {
      if (config.ENABLED) {
        console.error(`${prefix} ${message}`, fmt(error));
      }
    },

    startTimer: (label: string): { label: string; start: number } => {
      const timer = { label, start: performance.now() };
      if (config.ENABLED && config.LOG_PERFORMANCE) {
        console.log(`${prefix} START: ${label}`);
      }
      return timer;
    },

    endTimer: (timer: { label: string; start: number }, details?: Record<string, unknown>): number => {
      const duration = performance.now() - timer.start;
      if (config.ENABLED && config.LOG_PERFORMANCE) {
        const threshold = config.PERFORMANCE_THRESHOLD_MS ?? 1000;
        const isSlowOperation = duration > threshold;
        console.log(`${prefix} END: ${timer.label} ${duration.toFixed(2)}ms`, fmt(details));
        if (isSlowOperation) {
          console.warn(`${prefix} Slow operation: ${timer.label} took ${duration.toFixed(2)}ms`);
        }
      }
      return duration;
    },

    table: (label: string, data: unknown[]): void => {
      if (config.ENABLED && config.LOG_DATA) {
        console.log(`${prefix} ${label}:`);
        console.table(data);
      }
    },

    group: (label: string): void => {
      if (config.ENABLED) {
        console.group(`${prefix} ${label}`);
      }
    },

    groupEnd: (): void => {
      if (config.ENABLED) {
        console.groupEnd();
      }
    },
  };
}
